import { checkNested, waitFor } from 'utils/gw'

class HHVariantPicker extends HTMLElement {
  static defaultButtonLabel = window.variantStrings.addToCart

  constructor() {
    super()
    this.onVariantChange = this.onVariantChange.bind(this)
    this.onOptionKeyup = this.onOptionKeyup.bind(this)
    this.optionKeyMap = [...this.querySelectorAll('fieldset')].reduce(
      (acc, fieldset) => ({
        ...acc,
        [`option${fieldset.dataset.position}`]: fieldset.dataset.productOption,
      }),
      {}
    )
    this.sizeOptionKey = Object.keys(this.optionKeyMap).find(key => this.optionKeyMap[key] === 'Size')
    this.productId = this.dataset.productId
    // this.closest('stl-dialog')?.addEventListener('shop-the-look:modal-update', this.reset.bind(this))
  }

  connectedCallback() {
    // TODO: If this works, defer instead of preventing (dispatch event on transition complete)
    this.init()
  }

  disconnectedCallback() {
    this.destroy()
  }

  init() {
    console.log('DEBUG: INIT VARIANT PICKER', this)
    if (window.transitioning) return

    this.addEventListener('change', this.onVariantChange)
    Array.from(this.querySelectorAll('fieldset label')).forEach(label => {
      label.addEventListener('keyup', this.onOptionKeyup)
    })

    // Get initial variant
    const selectedVariantInput = this.querySelector('fieldset input:checked')
    if (selectedVariantInput) {
      this.updateOptions()
      this.updateMasterId()
    }
    this.variantMetafieldMessage(selectedVariantInput)

    document.dispatchEvent(new CustomEvent('variant:media-change'))
    this.initPreorderVariants()
    this.updateSwatches()
  }

  destroy() {
    this.removeEventListener('change', this.onVariantChange)
    Array.from(this.querySelectorAll('fieldset label')).forEach(label => {
      label.removeEventListener('keyup', this.onOptionKeyup)
    })
  }

  reset() {
    this.destroy()
    this.init()
  }

  get swatches() {
    return [...this.closest('.product').querySelectorAll('[data-swatch]')]
  }

  onOptionKeyup(e) {
    if (e.key !== 'Enter' && e.keyCode !== 13) return

    e.preventDefault()

    const targetId = e.target.getAttribute('for')
    this.querySelectorAll(`input`).forEach(input => (input.checked = false))
    this.querySelector(`input#${targetId}`).checked = true
    this.dispatchEvent(new Event('change'))
  }

  async initPreorderVariants() {
    try {
      const preorderVariantIds = []
      for (const input of this.querySelectorAll('fieldset input')) {
        if (!input.hasAttribute('data-preorder-ship-date')) continue

        const preorderShipDate = new Date(input.dataset.preorderShipDate)
        const now = new Date()

        if (now < preorderShipDate) {
          preorderVariantIds.push(input.dataset.variantId)
        }
      }

      this.preorderVariantIds = preorderVariantIds

      if (this.preorderVariantIds.length > 0 && this.productForm?.dataset.preorderBis === 'true') {
        await this.initPreorderBIS()
        this.onVariantChange()
      }
    } catch (error) {
      console.error('Error initializing preorder variants', error)
    }
  }

  // TODO: Fix this when dynamically changed via view transition
  async initPreorderBIS() {
    if (!checkNested(['BIS', 'popup', '_variantsById']) || Object.keys(window.BIS.popup._variantsById).length === 0) {
      await waitFor(
        () => checkNested(['BIS', 'popup', '_variantsById']) && Object.keys(window.BIS.popup._variantsById).length > 0,
        5000
      )
    }

    const variants = this.getVariantData()
    const variantsById = Object.fromEntries(variants.map(v => [v.id, v]))
    const variantsByTitle = Object.fromEntries(variants.map(v => [v.title, v]))

    window.BIS.popup._variantsById = variantsById
    window.BIS.popup._variantsByTitle = variantsByTitle

    const preorderVariants = this.preorderVariantIds.map(id => window.BIS.popup._variantsById[id])
    window.BIS.popup.variants = preorderVariants
    setTimeout(() => window.BIS.popup.createUI())
  }

  variantMetafieldMessage(selectedVariant) {
    if (!selectedVariant) return

    // Scope queries to the nearest product wrapper (works on PDP and in STL modal)
    const scope = this.closest('product-wrapper') || this.closest('.product--wrapper') || document

    const preorderPropertyInput = scope.querySelector('[name="properties[Pre-Order]"]')
    const preorderRangeInput = scope.querySelector('[name="properties[_PreorderRange]"]')
    const finalMessageInput = scope.querySelector('[name="properties[Final Sale]"]')
    const finalMessage = selectedVariant.getAttribute('data-final-sale-message')
    const preorderShipDate =
      selectedVariant.getAttribute('data-preorder-ship-date') &&
      new Date(`${selectedVariant.getAttribute('data-preorder-ship-date')}`)
    const preorderShipDateMsg = selectedVariant.getAttribute('data-preorder-ship-message')
    const preorderDateRange = selectedVariant.getAttribute('data-preorder-date-range')
    const buttonLabelPreOrder = window.variantStrings.preOrder ? window.variantStrings.preOrder : 'Pre-order'
    const buttonLabelAddToCart = window.variantStrings.addToCart ? window.variantStrings.addToCart : 'Add to cart'

    scope.querySelector('hh-product-form')?.classList.toggle('preorder-product-form', !!preorderShipDate)

    const preorderMessageEl = scope.querySelector('.variant-metafield--messages .preorder-message')
    if (preorderMessageEl) {
      preorderMessageEl.textContent = preorderShipDate
        ? `Ships ${preorderShipDateMsg} - Pre-order items will be charged in full at time of checkout.`
        : ''
    }

    if (this.currentVariant.available) {
      const submitButtons = [...scope.querySelectorAll('.product-form__buttons [type="submit"]')]
      const buttonLabel = preorderShipDate ? buttonLabelPreOrder : buttonLabelAddToCart
      for (const button of submitButtons) {
        button.textContent = buttonLabel
      }
    }

    if (preorderPropertyInput) {
      if (preorderShipDate) {
        preorderPropertyInput.removeAttribute('disabled')
        preorderPropertyInput.value = `Ships ${preorderShipDateMsg}`
      } else {
        preorderPropertyInput.setAttribute('disabled', true)
        preorderPropertyInput.value = ''
      }
    }

    if (preorderRangeInput) {
      if (preorderDateRange) {
        preorderRangeInput.removeAttribute('disabled')
        preorderRangeInput.value = preorderDateRange
      } else {
        preorderRangeInput.setAttribute('disabled', true)
        preorderRangeInput.value = ''
      }
    }

    // Determine if Final Sale should be applied
    const finalSaleResult = this.checkFinalSale(selectedVariant, finalMessage)

    // Update final sale message display
    let finalSaleText = ''
    if (finalSaleResult.isFinalSale) {
      // Use metafield message if available, otherwise use auto final sale message
      const message = finalSaleResult.message || 'Final Sale'
      finalSaleText = `${message}, no exchanges or returns.`
    }
    const finalMessageEl = scope.querySelector('.variant-metafield--messages .final-message')
    if (finalMessageEl) {
      finalMessageEl.textContent = finalSaleText
    }

    // Update line item property
    if (finalMessageInput) {
      if (finalSaleResult.isFinalSale) {
        finalMessageInput.removeAttribute('disabled')
        finalMessageInput.value = 'Final Sale'
      } else {
        finalMessageInput.setAttribute('disabled', true)
        finalMessageInput.value = ''
      }
    }
  }

  checkFinalSale(selectedVariant, metafieldFinalMessage) {
    if (!selectedVariant || !this.currentVariant) {
      return { isFinalSale: false, message: null }
    }

    // Check if manually set via metafield (priority 1)
    if (metafieldFinalMessage) {
      return { isFinalSale: true, message: metafieldFinalMessage }
    }

    // Check automatic final sale settings (priority 2)
    const autoFinalSaleEnabled = window.Shopify?.autoFinalSaleEnabled === true
    if (!autoFinalSaleEnabled) {
      return { isFinalSale: false, message: null }
    }

    // Check if product is on sale (compare_at_price > price)
    const isOnSale = this.currentVariant.compare_at_price > this.currentVariant.price
    if (!isOnSale) {
      return { isFinalSale: false, message: null }
    }

    // Check customer scope (all or international only)
    const customerScope = window.Shopify?.autoFinalSaleCustomerScope || 'international'
    const isInternational = window.Shopify?.country !== 'US'

    let shouldApplyFinalSale = false
    if (customerScope === 'all') {
      shouldApplyFinalSale = true
    } else if (customerScope === 'international' && isInternational) {
      shouldApplyFinalSale = true
    }

    if (shouldApplyFinalSale) {
      const autoMessage = window.Shopify?.autoFinalSaleMessage || 'Final Sale'
      return { isFinalSale: true, message: autoMessage }
    }

    return { isFinalSale: false, message: null }
  }

  get eventParent() {
    return this.closest('.stl-modal') ?? document
  }

  onVariantChange() {
    this.updateOptions()
    this.updateMasterId()
    // this.toggleAddButton(true, '', false)
    // this.updatePickupAvailability()

    setTimeout(() => {
      if (!this.currentVariant) {
        this.toggleAddButton(true, '', true, this.querySelector('fieldset input:checked'))
        this.setUnavailable()
      } else {
        this.updateVariantInput()
        this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut)
        this.updateMedia()
        if (!this.closest('stl-dialog')) {
          this.updateURL()
        }
        this.renderProductInfo()

        this.eventParent.dispatchEvent(
          new CustomEvent('variant:change', {
            detail: {
              productId: this.productId,
              variant: this.currentVariant,
              optionKeyMap: this.optionKeyMap,
            },
          })
        )
      }

      this.updateSwatches()
      this.variantMetafieldMessage(this.querySelector('fieldset input:checked'))
      desaturateSPILogo()

      const primaryForm = document.querySelector(`hh-product-form[data-form-id="product-form-${this.dataset.section}"]`)
      primaryForm?.updateCloneState()
    })
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'))
    if (fieldsets && fieldsets.length) {
      const options = fieldsets.map(
        fieldset => [...fieldset.querySelectorAll('input')].find(radio => radio.checked)?.value
      )

      // Apply options only if all are set
      if (!options.some(option => !option)) {
        console.log('APPLYING OPTIONS', options)
        this.options = options
      } else {
        console.log('NOT APPLYING OPTIONS - VALUE MISSING', options)
      }
    } else {
      this.options = Array.from(this.querySelectorAll('select'), select => select.value)
    }
  }

  updateMasterId() {
    if (!this.options) return

    this.currentVariant = this.getVariantData().find(variant => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option
        })
        .includes(false)
    })

    console.log('DEBUG: VARIANT CHANGED', this.currentVariant, this.options)

    this.closest('product-wrapper')?.querySelector('#BIS_trigger')?.setAttribute('data-variant-id', this.currentVariant.id)
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant?.featured_media) return
    const newMedia = document.querySelector(
      `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
    )
    if (!newMedia) return
    const parent = newMedia.parentElement
    parent.prepend(newMedia)
    window.setTimeout(() => {
      parent.scroll(0, 0)
    })
  }

  updateURL() {
    if (!this.currentVariant) {
      console.log('DEBUG: UPDATING URL WITHOUT VARIANT', this.dataset.url)
      document.dispatchEvent(new CustomEvent('variant:media-change'))
      window.history.replaceState({}, '', this.dataset.url)
      return
    }

    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set('variant', this.currentVariant.id)

    window.history.replaceState({}, '', `${this.dataset.url}?${searchParams.toString()}`)
  }

  updateSwatches() {
    for (const swatch of this.swatches) {
      const url = new URL(swatch.getAttribute('href'), window.location.origin)
      const queryParams = new URLSearchParams(window.location.search)
      queryParams.delete('variant')
      queryParams.forEach((value, key) => url.searchParams.set(key, value))

      if (!this.closest('stl-dialog')) {
        if (this.currentVariant && this.sizeOptionKey) {
          const selectedSize = this.currentVariant[this.sizeOptionKey]
            .split("|")[0]
            .trim();

          const sizeVariantMapJson = swatch.dataset.sizes;
          if (!sizeVariantMapJson) continue;

          const sizeVariantMap = JSON.parse(sizeVariantMapJson);
          const swatchVariantId = sizeVariantMap[selectedSize];

          url.searchParams.set("variant", swatchVariantId);
        }
      }
      const href = url.toString().replace(url.origin, '')
      swatch.setAttribute('href', href)
    }
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`)
    productForms.forEach(productForm => {
      const input = productForm.querySelector('input[name="id"]')
      input.value = this.currentVariant.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability')
    if (!pickUpAvailability) return

    if (this.currentVariant?.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id)
    } else {
      pickUpAvailability.removeAttribute('available')
      pickUpAvailability.innerHTML = ''
    }
  }

  renderProductInfo() {
    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
      .then(response => response.text())
      .then(responseText => {
        const id = `price-${this.dataset.section}`
        const idMobile = `price-${this.dataset.section}-mobile`
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const destination = document.getElementById(id)
        const destinationMobile = document.getElementById(idMobile)
        const source = html.getElementById(id)
        const sourceMobile = html.getElementById(idMobile)
        if (source && destination) destination.innerHTML = source.innerHTML
        if (sourceMobile && destinationMobile) destinationMobile.innerHTML = sourceMobile.innerHTML

        document.getElementById(`price-${this.dataset.section}`)?.classList.remove('visibility-hidden')
        this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut)
      })
  }

  toggleAddButton(
    disable = true,
    text,
    modifyClass = true,
    selectedVariant = this.querySelector('fieldset input:checked')
  ) {
    let buttonText = text

    const variantId = selectedVariant?.dataset.variantId
    const isPreorder = this.preorderVariantIds.includes(variantId)
    let preorderShipDate = selectedVariant?.dataset.preorderShipDate

    if (!disable) {
      // TODO: Update to use isPreorder once preorder implementation updated
      buttonText = !preorderShipDate ? HHVariantPicker.defaultButtonLabel : null
    }

    const productForms = [
      ...document.querySelectorAll(`hh-product-form[data-form-id="product-form-${this.dataset.section}"]`),
    ]
    productForms.forEach(async productForm => {
      // Ensure product form is defined before calling setButtonState
      if (!('setButtonState' in productForm)) {
        await customElements.whenDefined(productForm.localName)
      }

      productForm.setButtonState(!disable, buttonText, isPreorder)
    })

    if (!modifyClass) return
  }

  get productForm() {
    this._productForm ??= document.getElementById(`product-form-${this.dataset.section}`)?.closest('hh-product-form')
    return this._productForm
  }

  setUnavailable() {
    // Do not set unavailable if any options are missing selection
    if (!this.options) return

    const addButton = this.productForm?.querySelector('[name="add"]')
    if (!addButton) return
    addButton.textContent = window.variantStrings.unavailable
    document.getElementById(`price-${this.dataset.section}`)?.classList.add('visibility-hidden')
  }

  getProductData() {
    if (!this.productData) {
      this.productData = JSON.parse(this.querySelector('[data-product-data]').textContent)
      this.productData.variants = this.getVariantData()
    }

    return this.productData
  }

  getSoldOutProductData() {
    if (!this.soldOutProductData) {
      this.soldOutProductData = JSON.parse(this.querySelector('[data-product-data]').textContent)
      this.soldOutProductData.variants = this.getVariantData().filter(variant => !variant.available)
    }

    return this.soldOutProductData
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[data-product-variants-data]').textContent)
    return this.variantData
  }
}

if (!customElements.get('hh-variant-picker')) {
  customElements.define('hh-variant-picker', HHVariantPicker)
}

// Helpers

function desaturateSPILogo() {
  const spiBanner = document.querySelector('shopify-payment-terms')

  if (spiBanner?.shadowRoot?.querySelector('shop-pay-installments-banner')) {
    const spiBannerShadow = spiBanner.shadowRoot.querySelector('shop-pay-installments-banner').shadowRoot
    const newBannerSheet = new CSSStyleSheet()
    const newImageSheet = new CSSStyleSheet()
    const spiLogoShadow = spiBannerShadow.querySelector('shop-pay-logo').shadowRoot

    newBannerSheet.insertRule('.shopify-installments { margin-top: 1rem; margin-bottom: 1.5rem; }')
    newImageSheet.insertRule('.shop-pay-logo { filter: brightness(0%)!important; }')

    spiBannerShadow.adoptedStyleSheets.push(newBannerSheet)
    spiLogoShadow.adoptedStyleSheets.push(newImageSheet)
  }
}
desaturateSPILogo()
